import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "../../lib/api";

export const ChangeKeyPanel = ({ adminKey, onChanged, onUnauthorized }) => {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [tfa, setTfa] = useState({ enabled: false, admin_email: "" });
  const [tfaSaving, setTfaSaving] = useState(false);

  const loadTfa = useCallback(async () => {
    const res = await api.get("/admin/2fa", { headers: { "X-TAG-Admin-Key": adminKey }, validateStatus: () => true });
    if (res.status === 401) { onUnauthorized(); return; }
    if (res.data) setTfa(res.data);
  }, [adminKey, onUnauthorized]);

  useEffect(() => { loadTfa(); }, [loadTfa]);

  const saveTfa = async (e) => {
    e.preventDefault();
    setTfaSaving(true);
    const res = await api.post("/admin/2fa", tfa, { headers: { "X-TAG-Admin-Key": adminKey }, validateStatus: () => true });
    setTfaSaving(false);
    if (res.status === 401) { onUnauthorized(); return; }
    if (res.status >= 200 && res.status < 300) {
      toast.success(`2FA ${tfa.enabled ? "enabled" : "disabled"}.`);
    } else {
      toast.error("Could not save 2FA settings.");
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (current !== adminKey) return toast.error("Current key doesn't match.");
    if (next.length < 12) return toast.error("New key must be at least 12 characters.");
    if (next !== confirm) return toast.error("New key and confirmation don't match.");
    setSubmitting(true);
    const res = await api.post(
      "/admin/change-key",
      { current_key: current, new_key: next },
      { headers: { "X-TAG-Admin-Key": adminKey }, validateStatus: () => true }
    );
    setSubmitting(false);
    if (res.status === 401) {
      onUnauthorized();
      return;
    }
    if (res.status >= 200 && res.status < 300) {
      toast.success("Key changed. Your session is now using the new key.");
      onChanged(next);
      setCurrent("");
      setNext("");
      setConfirm("");
    } else {
      toast.error(res.data?.detail || "Change failed.");
    }
  };

  return (
    <div data-testid="settings-panel">
      <h2 style={{ color: "#fff", fontSize: 16, fontWeight: 700, margin: "0 0 6px" }}>Settings</h2>
      <p style={{ color: "#aaa", fontSize: 13, margin: "0 0 20px" }}>
        Manage your admin credentials. Changes are saved to the database and used immediately — no restart required.
      </p>

      <div className="tag-card" data-testid="change-key-card" style={{ maxWidth: 520, padding: 28 }}>
        <h3 style={{ color: "#fff", fontSize: 14, fontWeight: 700, margin: "0 0 16px" }}>Change admin key</h3>
        <form onSubmit={submit}>
          <Field label="Current key">
            <input
              data-testid="ck-current"
              type="password"
              className="tag-input"
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              placeholder="Your current admin key"
              autoComplete="current-password"
            />
          </Field>
          <Field label="New key (min 12 characters)">
            <input
              data-testid="ck-new"
              type="password"
              className="tag-input"
              value={next}
              onChange={(e) => setNext(e.target.value)}
              placeholder="At least 12 characters"
              autoComplete="new-password"
            />
          </Field>
          <Field label="Confirm new key">
            <input
              data-testid="ck-confirm"
              type="password"
              className="tag-input"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              placeholder="Re-enter new key"
              autoComplete="new-password"
            />
          </Field>
          <button
            data-testid="ck-submit"
            type="submit"
            className="btn-primary"
            disabled={submitting || !current || !next || !confirm}
            style={{ marginTop: 6 }}
          >
            {submitting ? "Saving…" : "Update key"}
          </button>
          <p style={{ color: "#777", fontSize: 11, marginTop: 14 }}>
            Tip: store your key in a password manager. If you lose it, you'll need to reset it via the server's <code style={{ color: "#aaa" }}>backend/.env</code> file.
          </p>
        </form>
      </div>

      {/* 2FA */}
      <div className="tag-card" data-testid="tfa-card" style={{ maxWidth: 520, padding: 28, marginTop: 16 }}>
        <h3 style={{ color: "#fff", fontSize: 14, fontWeight: 700, margin: "0 0 6px" }}>Two-factor authentication</h3>
        <p style={{ color: "#aaa", fontSize: 12, margin: "0 0 16px", lineHeight: 1.6 }}>
          When enabled, sign-in requires both your key AND a 6-digit code emailed to your admin address. Codes expire in 10 minutes.
        </p>
        <form onSubmit={saveTfa}>
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 14, padding: 12, background: "#0d0d0d", border: "1px solid #1a1a1a", borderRadius: 8 }}>
            <input
              data-testid="tfa-toggle"
              type="checkbox"
              checked={!!tfa.enabled}
              onChange={(e) => setTfa({ ...tfa, enabled: e.target.checked })}
              style={{ width: 18, height: 18, accentColor: "#FF6575", cursor: "pointer" }}
            />
            <label style={{ color: "#fff", fontSize: 13, fontWeight: 600, cursor: "pointer", flex: 1 }} onClick={() => setTfa({ ...tfa, enabled: !tfa.enabled })}>
              Require email code on every sign-in
            </label>
          </div>
          <Field label="Admin email (where codes are sent)">
            <input
              data-testid="tfa-email"
              type="email"
              className="tag-input"
              value={tfa.admin_email || ""}
              onChange={(e) => setTfa({ ...tfa, admin_email: e.target.value })}
              placeholder="support@theauthoritygap.co"
            />
          </Field>
          <button data-testid="tfa-save" type="submit" className="btn-primary" disabled={tfaSaving}>
            {tfaSaving ? "Saving…" : "Save 2FA settings"}
          </button>
          <p style={{ color: "#777", fontSize: 11, marginTop: 14 }}>
            ⚠ Make sure your SendGrid sender is verified before enabling — otherwise the code email won't deliver and you'll lock yourself out.
          </p>
        </form>
      </div>
    </div>
  );
};

const Field = ({ label, children }) => (
  <div style={{ marginBottom: 14 }}>
    <label style={{ display: "block", color: "#888", fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 6 }}>{label}</label>
    {children}
  </div>
);
